import type { CardId } from "@/types/card"
import type { FamilyNumber } from "@/theme/colors"

/*
 * The deck's card families, from the prototype's `CATS`. Names and card lists
 * are content, not configuration: a session stores only the numbers it plays,
 * and every screen reads its labels from here.
 *
 * The deck was re-cut from seven families to six (see `cardsRound`/`cards`): the
 * Monitors family is RETIRED, not deleted — `retired: true` hides it from new
 * sessions and the deck browser, but it stays in this table (with its colour and
 * its cards) so a session that already played it still resolves and renders. The
 * printed family numbers also shifted (Reflects 6→5, Partners 7→6); that
 * renumbering is display-only and lives in `cardNumber`, NOT in `n`, which is the
 * stable id family every stored card id is built from.
 */
export interface Family {
  /** Stable id family — card ids are `f<n>c…`, colours are keyed by it. Never
   *  renumbered: the printed number is display-only, see `cardNumber`. */
  n: FamilyNumber
  /** What the picker shows. The full name is too long for a row. */
  short: string
  name: string
  theme: string
  /** One line on what the family is for. Heads its section in the deck spread. */
  blurb: string
  /**
   * The family's active cards, in printed order. An explicit list rather than a
   * count because the re-cut left gaps and reorderings: family 1 keeps c1, c2, c5,
   * c3 (c4 retired), and family 4 prints c5 second. The retired card ids stay in
   * `CARD_FACES` so old sessions resolve; they are just absent here.
   */
  cardIds: readonly CardId[]
  /** Retired from the deck: hidden from new sessions and the browser, still
   *  resolvable for sessions that already played it. Monitors only. */
  retired?: boolean
}

export const FAMILIES: readonly Family[] = [
  {
    n: 1,
    short: "Sees & Interprets",
    name: "AI Sees, Understands & Interprets",
    theme: "Understand the World",
    blurb:
      "Sensing and making sense of the world and the data around it — images, sound, documents, sensors.",
    // 1.4 is Read & Interpret (c3), confirmed as the real fourth Sees capability
    // after the print export left it ambiguous; c4 (Sense the Environment) stays
    // retired.
    cardIds: ["f1c1", "f1c2", "f1c5", "f1c3"],
  },
  {
    n: 2,
    short: "Writes & Communicates",
    name: "AI Writes, Summarizes & Communicates",
    theme: "Communicate & Connect",
    blurb:
      "Turning ideas into clear communication across people and systems — text, voice, dialogue, tone.",
    cardIds: ["f2c1", "f2c2", "f2c3", "f2c4"],
  },
  {
    n: 3,
    short: "Analyzes & Reasons",
    name: "AI Analyzes & Generates Insight",
    theme: "Think & Reason",
    blurb:
      "Finding patterns, weighing options and making smarter calls — retrieval, planning, prediction.",
    cardIds: ["f3c1", "f3c2", "f3c3", "f3c4"],
  },
  {
    n: 4,
    short: "Acts & Automates",
    name: "AI Acts, Automates & Prepares",
    theme: "Take Action",
    blurb: "Doing the work — triggering actions, executing tasks, orchestrating whole workflows.",
    // Printed order, not id order: 4.2 is c5 (Monitor & Adjust Actions); c2
    // (Execute Tasks) is retired.
    cardIds: ["f4c1", "f4c5", "f4c3", "f4c4"],
  },
  {
    n: 5,
    short: "Monitors & Signals",
    name: "AI Monitors, Identifies & Signals Change",
    theme: "Detect & Alert",
    blurb:
      "Catching risks, anomalies and openings that need a human — before anyone thought to look.",
    cardIds: ["f5c1", "f5c2", "f5c3", "f5c4"],
    // Retired in the six-family re-cut. Kept so sessions that played it resolve.
    retired: true,
  },
  {
    n: 6,
    short: "Reflects & Coaches",
    name: "AI Reflects, Clarifies & Coaches",
    theme: "Explain & Guide",
    blurb: "Explaining itself and giving people usable context — the family that builds trust.",
    cardIds: ["f6c1", "f6c2", "f6c3", "f6c4"],
  },
  {
    n: 7,
    short: "Partners & Enhances",
    name: "AI Partners, Enhances Human Creativity & Decision-Making",
    theme: "Collaborate",
    blurb:
      "A partner in creativity and decisions, not a replacement — co-creation, decision support.",
    cardIds: ["f7c1", "f7c2", "f7c3", "f7c4"],
  },
] as const

/**
 * The order families are DEALT and browsed in.
 *
 * It now follows the printed numbering — Sees & Interprets (the green family) is
 * first, Writes & Communicates second — so every view agrees: the landing fan,
 * the whole-deck spread, the family rail and the in-session deal all lead with
 * family 1. The prototype's `FAM_ORDER` led with Writes (the family a room
 * recognises itself in fastest), but the re-cut deck is reviewed and run in
 * printed order, and the split order read as "family 1 shown in slot 2".
 */
export const FAMILY_DEAL_ORDER: readonly FamilyNumber[] = [1, 2, 3, 4, 5, 6, 7]

function dealRank(n: number): number {
  const index = FAMILY_DEAL_ORDER.indexOf(n as FamilyNumber)
  return index < 0 ? FAMILY_DEAL_ORDER.length : index
}

/**
 * The families a session plays, in deal order.
 *
 * Every screen that shows the deck as a deck — the deal, the spread, the family
 * jump bar — goes through this, so they cannot disagree about what "next family"
 * means.
 */
export function familiesInPlay(families: number[]): readonly Family[] {
  return FAMILIES.filter((family) => families.includes(family.n)).sort(
    (a, b) => dealRank(a.n) - dealRank(b.n)
  )
}

/**
 * The families a facilitator can tick for a session: every active family, plus
 * any retired one THIS session already plays (so a legacy Monitors session can
 * still untick it, but a new one cannot add it).
 *
 * This is the set the picker renders AND the denominator the summary counts
 * against — shared so the two cannot drift apart. They did: the summary counted
 * `FAMILIES.length` (7, retired Monitors included) and read "6 of 7 families"
 * for a full six-family deck that should say "All 6".
 */
export function selectableFamilies(families: number[]): readonly Family[] {
  return FAMILIES.filter((family) => !family.retired || families.includes(family.n))
}

/** How many cards the chosen families put in play. The ids themselves are in
 *  `content/cards`, which is what a pick or a placement stores. */
export function cardCount(families: number[]): number {
  return FAMILIES.filter((family) => families.includes(family.n)).reduce(
    (total, family) => total + family.cardIds.length,
    0
  )
}
